import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";

export const CANCEL_IDS = {
  trigger: "settings-cancel-membership-button",
  dialog: "cancel-membership-dialog",
  dispositionKeep: "cancel-disposition-keep",
  dispositionDonate: "cancel-disposition-donate",
  dispositionConnect: "cancel-disposition-connect",
  dispositionPrior: "cancel-disposition-prior",
  priorSelect: "cancel-prior-destination-select",
  confirm: "cancel-confirm-button",
};

export default function CancelMembershipDialog({ open, onOpenChange }) {
  const { refreshUser } = useAuth();
  const [disposition, setDisposition] = useState("keep");
  const [priors, setPriors] = useState([]);
  const [priorDest, setPriorDest] = useState("");
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState(0);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [{ data: w }, { data: d }] = await Promise.all([
          api.get("/wallet"),
          api.get("/wallet/destinations"),
        ]);
        setBalance(w.balance_cents || 0);
        setPriors(d.destinations || []);
        if ((d.destinations || []).length) setPriorDest(d.destinations[0].destination);
      } catch (err) {
        console.warn("Dialog preload failed", err);
      }
    })();
  }, [open]);

  const confirm = async () => {
    setBusy(true);
    try {
      const body = { wallet_disposition: disposition };
      if (disposition === "payout_prior") body.prior_destination = priorDest;
      const { data: res } = await api.post("/payments/cancel-subscription", body);
      await refreshUser();
      const moved = res.balance_moved_cents || 0;
      if (moved > 0) {
        if (disposition === "donate_alt_pool") toast.success(`Donated $${(moved / 100).toFixed(2)} to Alt Financier pool`);
        else if (disposition === "payout_connect") toast.success(`Cashout initiated · $${(moved / 100).toFixed(2)}`);
        else toast.success(`Manual cashout queued · $${(moved / 100).toFixed(2)}`);
      } else {
        toast.success("Membership will end at period end");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancellation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={CANCEL_IDS.dialog} className="bg-[#12141c] border-[#222634] text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle>Cancel membership</DialogTitle>
          <DialogDescription className="text-slate-400">
            Your membership will end at the current period&apos;s end. What should happen to your remaining CAI balance of{" "}
            <span className="font-mono text-yellow-300">${(balance / 100).toFixed(2)}</span>?
          </DialogDescription>
        </DialogHeader>
        <RadioGroup value={disposition} onValueChange={setDisposition} className="space-y-3">
          <div className="flex items-start gap-3">
            <RadioGroupItem data-testid={CANCEL_IDS.dispositionKeep} value="keep" id="keep" />
            <Label htmlFor="keep" className="cursor-pointer">
              <span className="text-slate-100 font-semibold">Keep in wallet</span>
              <p className="text-xs text-slate-400 mt-0.5">Leave the balance for future cashout.</p>
            </Label>
          </div>
          <div className="flex items-start gap-3">
            <RadioGroupItem data-testid={CANCEL_IDS.dispositionDonate} value="donate_alt_pool" id="donate" />
            <Label htmlFor="donate" className="cursor-pointer">
              <span className="text-slate-100 font-semibold">Donate to Alt Financier pool</span>
              <p className="text-xs text-slate-400 mt-0.5">Give back to the public pool other members can draw from.</p>
            </Label>
          </div>
          <div className="flex items-start gap-3">
            <RadioGroupItem data-testid={CANCEL_IDS.dispositionConnect} value="payout_connect" id="connect" />
            <Label htmlFor="connect" className="cursor-pointer">
              <span className="text-slate-100 font-semibold">Pay out to bank · Stripe Connect</span>
              <p className="text-xs text-slate-400 mt-0.5">Instant if your Connect account is active, otherwise queued.</p>
            </Label>
          </div>
          {priors.length > 0 && (
            <div className="flex items-start gap-3">
              <RadioGroupItem data-testid={CANCEL_IDS.dispositionPrior} value="payout_prior" id="prior" />
              <div className="flex-1">
                <Label htmlFor="prior" className="cursor-pointer">
                  <span className="text-slate-100 font-semibold">Pay out to a prior destination</span>
                </Label>
                <Select value={priorDest} onValueChange={setPriorDest}>
                  <SelectTrigger
                    data-testid={CANCEL_IDS.priorSelect}
                    className="mt-2 bg-[#0b0c10] border-[#222634] text-slate-100"
                    disabled={disposition !== "payout_prior"}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12141c] border-[#222634] text-slate-100">
                    {priors.map((p) => (
                      <SelectItem key={p.destination} value={p.destination}>
                        {p.destination}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </RadioGroup>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">
            Keep membership
          </Button>
          <Button
            data-testid={CANCEL_IDS.confirm}
            onClick={confirm}
            disabled={busy || (disposition === "payout_prior" && !priorDest)}
            className="rounded-full bg-rose-500 text-white hover:bg-rose-400"
          >
            {busy ? "Processing…" : "Cancel membership"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
